import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {LoadingService} from "../services/LoadingService";
import {TOAST_TYPE} from "../constants/Tpiq.constants";
import {ActivatedRoute, Router} from "@angular/router";
import {ToastService} from "../services/Toast.service";
import {StateService} from "../services/StateService";
import {SwitchBoard} from "../services/SwitchBoard";
import {takeUntil} from "rxjs/operators";
import {SignupService} from "../tpiq-services/signup.service";
import {Session} from "../services/Session";
import {SignupTermsComponent} from "./signup-terms/signup-terms.component";
import {SignupBannerComponent} from "./signup-banner/signup-banner.component";
import {PendingLogoService} from "../tpiq-services/pending-logo.service";
import {validatePassword} from "../utils/password.utils";
import {isValidEmail, isValidPhone, isValidUrl, hasDangerousContent} from "../utils/validators";


@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, FormsModule, SignupTermsComponent, SignupBannerComponent],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})
export class SignupComponent implements OnInit, OnDestroy {
  readonly isValidEmail        = isValidEmail;
  readonly isValidPhone        = isValidPhone;
  readonly isValidUrl          = isValidUrl;
  readonly hasDangerousContent = hasDangerousContent;

  org: any;
  user: string;
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  signupData: any = {};
  routeSub: any;
  sub: any;
  planExist: boolean = false;
  showPlanSection: boolean = false;
  planType = 'monthly';
  termsNotAccepted = false;
  reveal: any = {
    showTerms: false
  };
  cardTransactionInfo: any;
  protectedView = true;
  confirmPassword = '';
  passwordErrors: string[] = [];
  imageUrl = '';
  logoBg: any;
  displayLogo = true;
  subscriptionPlan: any;
  couponData: any;
  isFromHubspot = false;
  subscriptionId: string | null = null;

  constructor(
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private loadingService: LoadingService,
    private stateService: StateService,
    private _router: Router,
    private signupService: SignupService,
    private _route: ActivatedRoute,
    private pendingLogoService: PendingLogoService,
  ) {
  }

  ngOnInit() {
    this._inIt();
  }

  _inIt() {
    const base = this;
    this.routeSub = this._route.params.subscribe(params => {
      if (params.plan) {
        this.signupData.plan = params.plan;
        this.planExist = true;
      }
      if (params.couponcode) {
        this.signupData.couponcode = params.couponcode;
      }
    });

    this.sub = this._route.queryParams.subscribe(params => {
      if (params.plan) {
        this.signupData.plan = params.plan;
        this.planExist = true;
      }
      if (params.couponcode) {
        this.signupData.couponcode = params.couponcode;
      }
      // Internal/testing path only — was assuming Hubspot sending ? subscription_id= which is not in production.
      if (params.subscription_id) {
        this.isFromHubspot = true;
        this.subscriptionId = params.subscription_id;
        this.planExist = true;
        this.prefillFromSubscription(params.subscription_id);
      }
      // Production path: HubSpot Payment Link redirects to /signup?email=
      if (params.email) {
        this.isFromHubspot = true;
        this.signupData.email = params.email;
        // HubSpot payment links only pass email; other fields come from a backend CRM lookup
        this.signupData.firstName = params.first_name || this.signupData.firstName;
        this.signupData.lastName = params.last_name || this.signupData.lastName;
        this.signupData.companyName = params.company || this.signupData.companyName;
        this.signupData.phoneNumber = params.phone || this.signupData.phoneNumber;
        // Plan was already purchased via HubSpot — suppress the "No plan found" banner
        this.planExist = true;
        if (params.plan) {
          this.signupData.plan = params.plan;
        } else {
          // Temporary placeholder — overwritten by prefillFromHubspot() once the async
          // getContactPrefill() response arrives with the real plan from HubSpot CRM.
          this.signupData.plan = '';
        }
        // Fetch remaining contact fields from HubSpot CRM by email
        this.prefillFromHubspot(params.email);
      }
    });

    this.switchBoard.onClickPrev
      .pipe(takeUntil(this.destroy$)).subscribe(title => {
      this.gotoDefaultPage();
    });
  }

  prefillFromSubscription(subscriptionId: string) {
    this.loadingService.triggerLoadingEvent(true);
    this.signupService.getSubscriptionPrefill(subscriptionId)
      .subscribe({
        next: (contact) => {
          this.loadingService.triggerLoadingEvent(false);
          if (contact.email)     { this.signupData.email       = contact.email; }
          if (contact.firstName) { this.signupData.firstName   = contact.firstName; }
          if (contact.lastName)  { this.signupData.lastName    = contact.lastName; }
          if (contact.company)   { this.signupData.companyName = contact.company; }
          if (contact.phone)     { this.signupData.phoneNumber = contact.phone; }
          if (contact.plan)      { this.signupData.plan        = contact.plan; }
          this.sendBackupEmailOnce(`subscription:${subscriptionId}`);
        },
        error: () => {
          this.loadingService.triggerLoadingEvent(false);
          // Non-fatal: user can fill in missing fields manually
        },
      });
  }

  private sendBackupEmailOnce(key: string) {
    if (!this.signupData.email) { return; }
    const sentKey = `signup-backup-email:${key}`;
    const status = sessionStorage.getItem(sentKey);
    if (status === 'pending' || status === 'sent') { return; }
    sessionStorage.setItem(sentKey, 'pending');
    this.signupService.sendSignupBackupEmail(
      this.signupData.email,
      this.signupData.firstName || ''
    ).subscribe({
      next:  () => sessionStorage.setItem(sentKey, 'sent'),
      error: () => sessionStorage.removeItem(sentKey),
    });
  }

  prefillFromHubspot(email: string) {
    this.signupService.getContactPrefill(email)
      .subscribe({
        next: (contact) => {
          // Only overwrite fields that aren't already filled (e.g. from query params)
          if (contact.firstName && !this.signupData.firstName)   { this.signupData.firstName   = contact.firstName; }
          if (contact.lastName && !this.signupData.lastName)     { this.signupData.lastName    = contact.lastName; }
          if (contact.company && !this.signupData.companyName)   { this.signupData.companyName = contact.company; }
          if (contact.website && !this.signupData.websiteUrl)    { this.signupData.websiteUrl  = contact.website; }
          if (contact.phone && !this.signupData.phoneNumber)     { this.signupData.phoneNumber = contact.phone; }
          // If the backend found an active subscription, route through createFromSubscription()
          if (contact.subscriptionId) {
            this.subscriptionId = contact.subscriptionId;
            if (contact.plan) { this.signupData.plan = contact.plan; }
            this.sendBackupEmailOnce(`contact:${contact.subscriptionId}`);
          }
        },
        error: () => {
          // Non-fatal: user can fill in missing fields manually
        },
      });
  }

  getSubscriptionDetails() {
    this.loadingService.triggerLoadingEvent(true);
    this.signupService.getSubscriptionData(this.signupData.plan)
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.subscriptionPlan = response;
      }, (error) => {
        this.loadingService.triggerLoadingEvent(false);
        const message = error.message ? error.message : 'Failed to subscription data.';
        try {
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (e) {
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      });
  }

  getCouponDetails() {
    this.loadingService.triggerLoadingEvent(true);
    this.signupService.getCouponData(this.signupData.couponcode)
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.couponData = response;
      }, (error) => {
        this.loadingService.triggerLoadingEvent(false);
        const message = error.message ? error.message : 'Failed to coupon data.';
        try {
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (e) {
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  gotoDefaultPage() {
    const prevState = this.stateService.getPrevState();
    if (prevState) {
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    }
  }

  signup() {
    if (this.validateData()) {
      this.loadingService.triggerLoadingEvent(true);

      const call$ = (this.isFromHubspot && this.subscriptionId)
        ? this.signupService.createFromSubscription(this.subscriptionId, this.signupData)
        : this.signupService.signup(this.signupData);

      call$.subscribe({
        next: (response) => {
          this.loadingService.triggerLoadingEvent(false);
          Session.put('signupData', this.signupData);
          Session.put('isHubspotFlow', this.isFromHubspot);
          Session.put('cardTransactionInfo', { orgId: response.orgId, customerId: response.customerId });
          if (response.existingAccount) {
            // Firm already exists (Scenario B dedup) — auto-login will reset onboardingStatus
            // to null and route to /tpiq/onboard so the wizard resumes where they left off.
            this.toastService.pop(TOAST_TYPE.info, 'Account already exists. Resuming your setup...');
          }
          this._router.navigate(['auto-login']);
        },
        error: (error) => {
          this.loadingService.triggerLoadingEvent(false);
          // handleError in SignupService unwraps HttpErrorResponse to its body,
          // so error.error is the string from { error: "..." }, or error.message for other shapes.
          const message = (error?.error ?? error?.message ?? 'Failed to submit the data.') as string;
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      });
    }
  }

  validateData() {
    if (this.isFromHubspot && !this.subscriptionId) {
      this.toastService.pop(TOAST_TYPE.error, 'We could not confirm your subscription yet. Please retry from the payment link or use the backup email.');
      return false;
    }
    // HubSpot flow: plan was purchased before signup, skip plan validation
    if (!this.isFromHubspot && !this.signupData.plan) {
      this.toastService.pop(TOAST_TYPE.error, 'Plan must be required');
      return false;
    } else if (!this.signupData.firstName){
      this.toastService.pop(TOAST_TYPE.error, 'Enter First name');
      return false;
    } else if (hasDangerousContent(this.signupData.firstName)){
      this.toastService.pop(TOAST_TYPE.error, 'First name contains invalid characters');
      return false;
    } else if (!this.signupData.lastName){
      this.toastService.pop(TOAST_TYPE.error, 'Enter Last name');
      return false;
    } else if (hasDangerousContent(this.signupData.lastName)){
      this.toastService.pop(TOAST_TYPE.error, 'Last name contains invalid characters');
      return false;
    } else if (!this.signupData.companyName){
      this.toastService.pop(TOAST_TYPE.error, 'Enter Company name');
      return false;
    } else if (hasDangerousContent(this.signupData.companyName)){
      this.toastService.pop(TOAST_TYPE.error, 'Company name contains invalid characters');
      return false;
    } else if (!this.signupData.websiteUrl){
      this.toastService.pop(TOAST_TYPE.error, 'Enter Company website');
      return false;
    } else if (!this.isValidUrl(this.signupData.websiteUrl)){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter a valid website (e.g. www.yourcompany.com)');
      return false;
    } else if (!this.signupData.email){
      this.toastService.pop(TOAST_TYPE.error, 'Enter email');
      return false;
    } else if (this.signupData.email && !isValidEmail(this.signupData.email)){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter a valid email');
      return false;
    } else if (!this.signupData.phoneNumber){
      this.toastService.pop(TOAST_TYPE.error, 'Enter phone');
      return false;
    } else if (!this.isValidPhone(this.signupData.phoneNumber)){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter a valid 10-digit phone number');
      return false;
    } else if (!this.signupData.desiredPassword){
      this.toastService.pop(TOAST_TYPE.error, 'Enter password');
      return false;
    }
    this.passwordErrors = validatePassword(this.signupData.desiredPassword);
    if (this.passwordErrors.length > 0) { return false; }
    if (this.signupData.desiredPassword !== this.confirmPassword) {
      this.toastService.pop(TOAST_TYPE.error, 'Passwords do not match');
      return false;
    }
    return true;
  }

  onLogoFileChange($event: Event) {
    const input = $event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) { return; }
    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      this.toastService.pop(TOAST_TYPE.error, 'Only PNG, JPEG, and SVG files are supported.');
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.toastService.pop(TOAST_TYPE.error, 'Logo must be 5MB or smaller.');
      input.value = '';
      return;
    }
    this.pendingLogoService.set(file);
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const dataUrl: string = e.target.result;
      this.imageUrl = dataUrl;
      this.displayLogo = false;
      this.logoBg = {
        background: 'url("' + this.imageUrl + '") center center no-repeat',
        'background-size': 'contain'
      };
      setTimeout(() => { this.displayLogo = true; }, 5);
    };
    reader.readAsDataURL(file);
  }

  selectPlan(plan) {
    let planName = this.getPlanData(plan);
    setTimeout(() => {
      this.savePlan(planName);
    });
  }

  savePlan(plan) {
    let customerId = this.cardTransactionInfo.customerId || '';
    if (customerId) {
      this.loadingService.triggerLoadingEvent(true);
      this.signupService.saveSubscriptionPlanData(customerId, plan)
        .subscribe((response) => {
          this.loadingService.triggerLoadingEvent(false);
          window.open(this.cardTransactionInfo.cardCollectionPageUrl, '_self');
        }, (error) => {
          this.loadingService.triggerLoadingEvent(false);
          const message = error.message ? error.message : 'Failed to update the plan data.';
          try {
            this.toastService.pop(TOAST_TYPE.error, message);
          } catch (e) {
            this.toastService.pop(TOAST_TYPE.error, message);
          }
        });
    }
  }

  getPlanData(selectedPlan) {
    let plan: any;

    if (this.planType === 'monthly') {
      if (selectedPlan === 'basic') {
        plan = 'Basic-Plan-397-USD-Monthly';
      } else if (selectedPlan === 'standard') {
        plan = 'Standard-Plan-697-USD-Monthly';
      } else  if (selectedPlan === 'growth'){
        plan = 'Growth-Plan-997-USD-Monthly';
      }
    } else if (this.planType === 'yearly') {
      if (selectedPlan === 'basic') {
        plan = 'Basic-Plan-4049-USD-Yearly';
      } else if (selectedPlan === 'standard') {
        plan = 'Standard-Plan-7109-USD-Yearly';
      } else  if (selectedPlan === 'growth'){
        plan = 'Growth-Plan-10169-USD-Yearly';
      }
    }
    return plan;
  }

  selectPlanType(type) {
    this.planType = type;
  }

  showTermsPopup() {
    this.reveal.showTerms = true;
  }

  hideReveal(key){
    this.reveal[key] = false;
  }

  onLoadTransactionPortal() {
    const base = this;
    setTimeout(() => {
      base.loadingService.triggerLoadingEvent(false);
    }, 3000);
  }

  acceptTerms() {
    this.signupData.acceptTerms = true;
    this.reveal.showTerms = false;
  }

  showProtectedView() {
    this.protectedView = !this.protectedView;
  }

  toggleTermsValid() {
    if (this.termsNotAccepted) {
      this.termsNotAccepted = false;
    }
  }

}
